/**
 * Command Dock — 센서 광학 / 이미저리 / 뷰 토글.
 * God's Eye View 의 하단 command dock 구조를 차용. 좌측은 LayerControls 가
 * 이미 차지하므로 지도 영역 하단 중앙에 가로로 배치한다.
 */
import { type FC, useEffect } from 'react';
import { useAppStore } from '@/stores/app-store';
import { SENSOR_PRESETS, sensorAccent } from '@/components/map/sensor-optics';
import { GLOBE_BASEMAPS } from '@/config/map-styles';

const Divider: FC<{ accent: string }> = ({ accent }) => (
  <span className="w-px h-6 shrink-0" style={{ background: `${accent}33` }} />
);

const GroupLabel: FC<{ children: string }> = ({ children }) => (
  <span className="font-mono text-[8px] tracking-[0.2em] text-slate-600 shrink-0 hidden lg:inline">
    {children}
  </span>
);

export const SensorRail: FC = () => {
  const sensorMode      = useAppStore((s) => s.sensorMode);
  const setSensorMode   = useAppStore((s) => s.setSensorMode);
  const globeBasemap    = useAppStore((s) => s.globeBasemap);
  const setGlobeBasemap = useAppStore((s) => s.setGlobeBasemap);
  const hudEnabled      = useAppStore((s) => s.hudEnabled);
  const toggleHud       = useAppStore((s) => s.toggleHud);
  const globeMode       = useAppStore((s) => s.globeMode);
  const toggleGlobeMode = useAppStore((s) => s.toggleGlobeMode);

  const accent = sensorAccent(sensorMode);

  // ── 단축키: 1-6 센서, H = HUD, G = 글로브 ──────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const preset = SENSOR_PRESETS.find((p) => p.hotkey === e.key);
      if (preset) { setSensorMode(preset.id); return; }
      if (e.key === 'h' || e.key === 'H') toggleHud();
      if (e.key === 'g' || e.key === 'G') toggleGlobeMode();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setSensorMode, toggleHud, toggleGlobeMode]);

  const chip = (
    key: string,
    label: string,
    active: boolean,
    color: string,
    onClick: () => void,
    title: string,
    hotkey?: string,
  ) => (
    <button
      key={key}
      onClick={onClick}
      title={title}
      className="group relative px-2 py-1 rounded-sm shrink-0 transition-colors hover:bg-surface-hover"
      style={{
        background: active ? `${color}1f` : 'transparent',
        boxShadow: active ? `inset 0 0 0 1px ${color}66` : undefined,
      }}
    >
      <span
        className="font-mono text-[10px] tracking-[0.14em] transition-colors"
        style={{ color: active ? color : 'rgb(100 116 139)' }}
      >
        {label}
      </span>
      {hotkey && (
        <span className="ml-1 font-mono text-[8px] text-slate-700 group-hover:text-slate-500">
          {hotkey}
        </span>
      )}
    </button>
  );

  return (
    <div className="absolute bottom-9 left-1/2 -translate-x-1/2 md:left-auto md:translate-x-0 md:right-[43%] lg:right-[39%] md:mr-10 z-30 pointer-events-auto max-w-[92vw]">
      <div className="glass-panel hud-corners flex items-center gap-1 px-2.5 py-1.5 overflow-x-auto">
        <GroupLabel>OPTICS</GroupLabel>
        {SENSOR_PRESETS.map((p) =>
          chip(p.id, p.code, p.id === sensorMode, p.accent, () => setSensorMode(p.id), `${p.label}  [${p.hotkey}]`, p.hotkey),
        )}

        <Divider accent={accent} />

        <GroupLabel>IMAGERY</GroupLabel>
        {GLOBE_BASEMAPS.map((b) =>
          chip(b.id, b.code, b.id === globeBasemap, accent, () => setGlobeBasemap(b.id), `${b.label} — ${b.attribution}`),
        )}

        <Divider accent={accent} />

        {chip('view', globeMode ? '3D' : '2D', globeMode, accent, toggleGlobeMode, 'Globe / Flat  [G]', 'G')}
        {chip('hud', 'HUD', hudEnabled, accent, toggleHud, 'Tactical HUD  [H]', 'H')}
      </div>
    </div>
  );
};
